import {
    BlurEvent, Button, ChangeEvent, Checkbox, CityState, ClickEvent, Component, DataSourceExecutionEvent, DataSourceMode, DropdownItem, Label,
    LookupModelSelectionEvent, Panel, ScreenStack, Snackbar, Textbox
} from "@mcleod/components";
import {
    Alignment,
    Api, CityUtil, CurrencySettings, DateUtil, FieldUpdateEvent, FileUtil, HorizontalAlignment, LogManager, ModelRow,
    ModelSearchResult, StringUtil
} from "@mcleod/core";
import { LMEGeneralSettings } from "@mcleod/general";
import { AddCarrier, CarrierOfferInfo, OnboardCarrier } from "@mcleod/dispatch";
import { SendLoadTender } from "@mcleod/datafusion";
import { DispatchCommentsErrors } from "./DispatchCommentsErrors";
import { AutogenLayoutCarrierAssignment } from "./autogen/AutogenLayoutCarrierAssignment";
import { ContactVerificationUtil } from "./ContactVerificationUtil";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";

const log = LogManager.getLogger("powerbroker/CarrierAssignment");

export class CarrierAssignment extends AutogenLayoutCarrierAssignment {
        private fileType: DropdownItem[] = [ new DropdownItem("P", "PDF"), new DropdownItem("N", "PNG")];
    private _origPendingPayeeId;
    private origPendingCarrierNbr;
    public doAfterClose: (canceled: boolean) => void;
    public carrierPayCallback: (canceled: boolean) => void;
    private _listenersActive = false;
    private preventAssignment: boolean;
    private _qualError: string;
    private _payError: string;
    private carrierQualSnackbar: Snackbar;
    private postingRow: boolean;
    public carrierOfferInfo: CarrierOfferInfo;
    public isIntercompany: boolean;
    public carrierId: string;

    private vendor_id: string;
    private packet_website_url: string;
    private carrierins_control_onboarding_server: string;

    private auto_onboard_vendors = ['D', 'H', 'R'];

    private add_vendors = ['S', 'C', 'P'];

    private get layoutCommentsErrors(): DispatchCommentsErrors {
        return this.layoutDispatchCommentsErrors as DispatchCommentsErrors;
    }

    public get origPendingPayeeId() {
        return this._origPendingPayeeId;
    }

    public set origPendingPayeeId(value) {
        this._origPendingPayeeId = value;
    }

    private get listenersActive() {
        return this._listenersActive;
    }

    private set listenersActive(value) {
        this._listenersActive = value ? value : !this._listenersActive;
    }

    private get qualError(): string {
        return this._qualError;
    }

    private set qualError(value: string) {
        if (value != this.qualError) {
            this._qualError = value;
            this.layoutDispatchCommentsErrors.addValidationErrors([{ message: this.payError }]);
            if (this.qualError == null && this.carrierQualSnackbar != null) {
                this.carrierQualSnackbar.dismiss();
                this.carrierQualSnackbar = null;
            }
            this.configureSaveButton();
        }
    }

    public get payError(): string {
        return this._payError;
    }

    private set payError(value: string) {
        if (value != this.payError) {
            this._payError = value;
            if (value == null) {
                const payeeId = this.activeRow?.get("pending_payee_id", this.activeRow?.get("override_payee_id"));
                this.layoutDispatchCommentsErrors.displayValidationErrors(this.activeRow?.get("id"), payeeId, null, null,
                    this.activeRow?.get("carrier_contact"), this.activeRow?.get("carrier_email"));
            } else {
                this.layoutDispatchCommentsErrors.addValidationErrors([{ message: this.payError }]);
            }
            this.configureSaveButton();
            if (this.carrierPayCallback)
                this.carrierPayCallback(this._payError != null);
            }
    }

    override onLoad() {
        this.dfLabel.visible = false;
        this.panelUnknownCarrier.visible = false;
        this.textboxFileType.items = this.fileType;

        if (this.textboxFileType.items && this.textboxFileType.items.length > 0) {
            this.textboxFileType.selectedItem = this.textboxFileType.items[0];
        }

        if (!this.isIntercompany) {
            this.textboxPendingPayeeId.addDropdownAdditionalAction({
                caption: "Create Pending Assignment",
                fontSize: "large", fontBold: true,
                onClick: () => {
                    this.setPanelUnknownCarrierVisible(true);
                    this.calculatePay(false, false, true);
                }
            });
        }

        this.textboxPendingPayeeId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (!event.hasSelection()) {
                return;
            }
            const selectedRow = event.selection;
            this.activeRow.setValues({
                "carrier_contact": selectedRow.get("contact.name"),
                "carrier_phone": selectedRow.get("contact.phone"),
                "carrier_email": selectedRow.get("contact.email"),
                "contact_id": selectedRow.get("contact.id"),
            });
            const row = selectedRow.getFirstLookupModelData("carrier_contact");
            ContactVerificationUtil.updateVerificationImage(row, this.imageCarrierVerification);
            this.activeRow.setLookupModelData("carrier_name", { "name": selectedRow.get("name") });
            this.mainDataSource.displayDataInBoundComponents();
        });

        const header = this.tabsetContent["_header"];
        if (header != null)
            header.visible = false
        this.mainDataSource.addDisplayListener(event => {
            if (this.mainDataSource.activeRow?.get("carrier_contact") != null) {
                this.mainDataSource.activeRow.setLookupModelData("carrier_contact", {
                    "name": this.mainDataSource.activeRow.get("carrier_contact", ""),
                    "id": this.mainDataSource.activeRow.get("contact_id", ""),
                });
            }
            if (this.mainDataSource.activeRow?.get("override_driver_nm") != null) {
                this.mainDataSource.activeRow.setLookupModelData("override_driver_nm", {
                    "driver_name": this.mainDataSource.activeRow.get("override_driver_nm", "")
                });
            }
            if (this.mainDataSource.activeRow?.get("override_driver2_nm") != null) {
                this.mainDataSource.activeRow.setLookupModelData("override_driver2_nm", {
                    "driver_name": this.mainDataSource.activeRow.get("override_driver2_nm", "")
                });
            }
        })
        this.textboxCarrierContact.addBeforeLookupModelSearchListener(event => {
            const payeeId = this.activeRow?.get("pending_payee_id", this.activeRow?.get("override_payee_id"));
            if (payeeId == null) {
                event.preventDefault();
            } else {
                event.filter.parent_row_id = payeeId;
                event.filter.parent_row_type = "P";
            }
        });
        this.textboxCarrierContact.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (!event.hasSelection()) return;
            const selectedRow = event.selection;
            this.mainDataSource.activeRow.set("carrier_phone", selectedRow.get("phone"))
            this.mainDataSource.activeRow.set("carrier_email", selectedRow.get("email"))
            ContactVerificationUtil.updateVerificationImage(selectedRow, this.imageCarrierVerification);
            const payeeId = this.activeRow?.get("pending_payee_id", this.activeRow?.get("override_payee_id"));
            this.validateCarrierForDispatch(payeeId, true, true, true);
        });
        this.textboxCarrierContact.addBlurListener((event: BlurEvent) => {
            const textbox = event.target as Textbox;
            if (textbox.isEmpty()) {
                this.activeRow.set("carrier_phone", null);
                this.activeRow.set("carrier_email", null);
                ContactVerificationUtil.updateVerificationImage(null, this.imageCarrierVerification);
                const payeeId = this.activeRow?.get("pending_payee_id", this.activeRow?.get("override_payee_id"));
                if (event.changedWhileFocused && !textbox.isDropdownVisible()) {
                    this.validateCarrierForDispatch(payeeId, true, true, true);
                }
            }
            else if (event.changedWhileFocused && !textbox.isDropdownVisible() && this.mainDataSource.activeRow.getFirstLookupModelData("carrier_contact").get("id") == null) {
                    const payeeId = this.activeRow?.get("pending_payee_id", this.activeRow?.get("override_payee_id"));
                    const name = this.mainDataSource.activeRow.getFirstLookupModelData("carrier_contact").get("name");
                    Api.search("lme/general/contact", { "parent_row_id": payeeId, "parent_row_type": "P", "name": name }).then((response) => {
                        const lmModelRow = new ModelRow("lme/general/contact", false, { ...response.data[0] });
                        ContactVerificationUtil.updateVerificationImage(lmModelRow, this.imageCarrierVerification);
                        this.validateCarrierForDispatch(payeeId, true, true, true);
                    });
            }
        });


        this.layoutMovementPay.layoutCarrierRates.replaceMainDatasource(this.mainDataSource);
        this.layoutMovementPay.carrierPayCallback = (errorMessage: string, errorDetails: string) => {
            if (errorDetails != null && this.activeRow?.isNull("override_payee_id")) {
                if (this.origPendingPayeeId == null)
                    errorDetails += ", assign the carrier as Pending, or select X to cancel."
                else
                    errorDetails += ", update the Pending assignment, or cancel it by removing the carrier.";
            }
            this.payError = errorMessage;
            return errorDetails;
        };
        this.layoutDriverInformation.afterDriverSelection = (selectedRow, driverNumberSuffx) => {
            if (!this.activeRow?.isNull("pending_payee_id")) {
                this.validateCarrierForDispatch(this.activeRow?.get("pending_payee_id"), false, true);
            }
            else if (!this.activeRow?.isNull("override_payee_id")) {
                this.validateCarrierForDispatch(this.activeRow?.get("override_payee_id"), false, true);
            }
        }
        let isPrequalified = false;
        this.textboxPendingCarrierId.addDataDisplayListener((event) => {
            if (!StringUtil.isEmptyString(this.textboxPendingCarrierId.text)) {
                if (!isPrequalified) {
                    this.prequalCarrier();
                    this.btnPrequalCarrier.visible = false;
                    isPrequalified = true;
                }
                if (!StringUtil.isEmptyString(this.textboxPendingCarrierIdType.text) && StringUtil.equalsIgnoreCase("MC Number", this.textboxPendingCarrierIdType.text)) {
                    this.setPendingInviteLabel(this.textboxPendingCarrierId.text, "icc_number");
                } else if (StringUtil.equalsIgnoreCase(this.textboxPendingCarrierIdType.text, "DOT Number")) {
                    this.setPendingInviteLabel(this.textboxPendingCarrierId.text, "dot_number");
                } else {
                    this.setPendingInviteLabel(this.textboxPendingCarrierId.text, "icc_number");
                }
            }

        });
    }

    textboxCarrierEmailOnBlur(event: BlurEvent) {
        if (event.changedWhileFocused) {
            this.searchContact();
        }
    }

    async searchContact(carrierID?: string) {
        const payeeId = carrierID ? carrierID : this.activeRow?.get("pending_payee_id", this.activeRow?.get("override_payee_id"));
        const response = await Api.search("lme/general/contact", {
            "parent_row_id": payeeId,
            "parent_row_type": "P",
            "name": this.activeRow?.get("carrier_contact"),
            "email": this.activeRow?.get("carrier_email")
        });
        const lmModelRow = new ModelRow("lme/general/contact", false, { ...response.data[0] });
        ContactVerificationUtil.updateVerificationImage(lmModelRow, this.imageCarrierVerification);
        await this.validateCarrierForDispatch(payeeId, false, true, true);
    }

    async sourceCarriersForAssignmentAfterExecution(event: DataSourceExecutionEvent) {
        const carrierId = this.sourceCarriersForAssignment.activeRow?.get("id");
        this.textboxFileType.items = this.fileType;

        let fileType = "P";
        if (!StringUtil.isEmptyString(carrierId)) {
            const path = "lme/powerbroker/smc3-profile-document-type";
            const response = await Api.search(path, { carrier_id: carrierId });
            const data = response?.data?.[0];
            fileType = data?.document_type_id ?? "P";
        }

        this.textboxFileType.setSelectedItemFromValue(fileType);
        if (!this.textboxFileType.selectedItem && this.textboxFileType.items?.length > 0) {
            this.textboxFileType.selectedItem = this.textboxFileType.items[0];
        }

        this.setStarRatingVisibility();
    }

    setStarRatingVisibility() {
        this.starRating1.visible = this.sourceCarriersForAssignment.activeRow?.get("rating_star_count") != null;
    }

    async loadTbPendingPayeeId(modelRow: ModelRow) {
        this.clearFields(modelRow == null);
        this.setPanelUnknownCarrierVisible(false);
        if (modelRow) {
            const payeeId = modelRow.get("id");

            if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                const currencyToUse: string = modelRow.get("currency") ?? CurrencySettings.getTransactionalCurrency();
                this.mainDataSource.activeRow?.set("currency_selected", currencyToUse);
                this.mainDataSource.activeRow?.setLookupModelData("currency_selected", { currency_type_id: currencyToUse });
                this.mainDataSource.displayDataInBoundComponent(this.layoutMovementPay.textboxCurrencyCodeSelector);
            }

            this.sourceCarriersForAssignment.setRowsAndMode(DataSourceMode.NONE, [modelRow]);
            this.setStarRatingVisibility();
            this.sourceCarriersForAssignment.displayDataInBoundComponents();
            const extraPayTable = this.layoutMovementPay.tableExtraPay;
            extraPayTable.quickAddData.setValueAndLookupModelData("payee_id", payeeId, { id: payeeId });
            const textboxPayeeId = extraPayTable.quickAddRow.findComponentById("textboxPayAdjustmentsPayeeId");
            textboxPayeeId.displayData(extraPayTable.quickAddData, null, 0);
            await this.validateCarrierForDispatch(payeeId, false, false, true, true);
            if (LMEGeneralSettings.getSingleton().getDispatchControl().rate_carrier == "Y") {
                await this.calculatePay(false, true, true);
                //For the contact that comes back from carrier lane rate
                this.textboxCarrierContact.displayData(this.mainDataSource.activeRow, null, 0);
            }
            if (modelRow.get("show_iso_icon")) {
                this.sourceCarrier.search({ "id": payeeId }).then(response => {
                    this.panelIsoScorecard.visible = response?.modelRows[0]?.data?.carrier_scorecard_exported;
                });
            }
        }
    }

    setPanelUnknownCarrierVisible(visible: boolean, clearData: boolean = true) {
        this.panelUnknownCarrier.visible = visible;
        this.textboxPendingCarrierIdType.required = visible;
        this.textboxPendingCarrierId.required = visible;
        this.btnPrequalCarrier.color = 'primary';
        if (visible) {
            if (this.origPendingCarrierNbr == null)
                this.clearBoundData(this.panelUnknownCarrier);

            const onlyDigitsEntered = /^\d+$/.test(this.textboxPendingPayeeId.text);
            if (StringUtil.isEmptyString(this.textboxPendingPayeeId.text)) {
                if (clearData) {
                    this.clearFields(true);
                }
            } else if (onlyDigitsEntered) {
                this.mainDataSource.activeRow.set("pending_carrier_nbr", this.textboxPendingPayeeId.text);
                this.mainDataSource.activeRow.set("pending_payee_id", null);
            } else {
                this.mainDataSource.activeRow.set("pending_carrier_name", this.textboxPendingPayeeId.text);
                this.mainDataSource.activeRow.set("pending_payee_id", null);
            }

            this.textboxPendingCarrierIdType.selectedItem = this.textboxPendingCarrierIdType.items[0];
            if (this.mainDataSource.activeRow?.getBoolean("has_pending_assignment", false))
                this.textboxPendingPayeeId.enabled = false;

            this.textboxPendingPayeeId.hideDropdown(true);
            this.textboxPendingCarrierName.focus();
            this.configureSaveButton();
            this.sourceCarrierInsCtrl.search()
                .then((result: ModelSearchResult) => {
                    if (result?.modelRows?.[0] !== undefined) {
                        this.vendor_id = result.modelRows[0].get('vendor_id');
                        this.packet_website_url = result.modelRows[0].get('packet_website_url');
                        this.carrierins_control_onboarding_server = result.modelRows[0].get('onboarding_server');
                        const vendorDesc: string = setVendorDesc(this.vendor_id);
                        this.textboxPrequalVendor.text = vendorDesc;
                        if (!StringUtil.isEmptyString(vendorDesc)) {
                            this.btnPrequalCarrier.tooltipDynamicContent = {
                                text: "Pre-qualify this carrier with " + vendorDesc + ".",
                                align: HorizontalAlignment.CENTER
                            };
                        } else {
                            this.btnPrequalCarrier.visible = false;
                            this.textboxPrequalVendor.visible = false;
                        }
                    } else {
                        this.btnPrequalCarrier.visible = false;
                        this.textboxPrequalVendor.visible = false;
                    }
                });
            if (!StringUtil.isEmptyString(this.textboxPrequalVendor.text) && !StringUtil.isEmptyString(this.textboxPendingCarrierId.text) && !StringUtil.isEmptyString(this.textboxPendingCarrierIdType.text)) {
                this.layoutCarrierPrequalResult.configurePreQualBtn(this.textboxPendingCarrierId, this.textboxPendingCarrierIdType, this.textboxPrequalVendor, this.btnPrequalCarrier, this.vendor_id);
            } else {
                this.btnPrequalCarrier.visible = false;
                this.textboxPrequalVendor.visible = false;
            }
        }
        if (!StringUtil.isEmptyString(this.textboxPendingCarrierId.text) && !StringUtil.isEmptyString(this.textboxPendingCarrierIdType.text)) {
            this.btnPrequalCarrier.visible = visible;
            this.textboxPrequalVendor.visible = visible;
        }
    }

    setPendingValuesFromOffer() {
        const pending_carrier_nbr = this.carrierOfferInfo.icc_number ?? this.carrierOfferInfo.dot_number;
        const pendingCarrierIdTypeIndex = !!this.carrierOfferInfo.icc_number ? 0 : 1;
        this.textboxPendingCarrierIdType.selectedItem = this.textboxPendingCarrierIdType.items[pendingCarrierIdTypeIndex];
        this.mainDataSource.activeRow.set("pending_carrier_nbr", pending_carrier_nbr);
        this.mainDataSource.activeRow.set("pending_carrier_name", this.carrierOfferInfo.carrier_name);
    }

    fieldUpdated(event: FieldUpdateEvent) {
        if (!this.postingRow && event.fieldName == "pending_payee_id" && event.originator != null && event.oldValue != event.newValue) {
            this.loadTbPendingPayeeId(event.originator.getFirstLookupModelData()).then(() => {
                const carrierId = event.newValue;
                this.configureSaveButton();
                if (carrierId == null) {
                    return;
                }
                this.savebuttonAssign.busy = false;
                this.setDFLabel(carrierId);
            });
        }
    }

    clearFields(clearCarrier: boolean = false) {
        this.listenersActive = false;
        this.clearErrors();
        this.clearBoundData(this.panelCarrierInfo);
        this.clearBoundData(this.panelContact);
        this.clearBoundData(this.panelEquipment);
        this.labelMilesToPickup.text = null;
        this.dfLabel.visible = false;
        this.sourceCarriersForAssignment.clear();
        this.sourceCarriersForAssignment.displayDataInBoundComponents();
        this.layoutMovementPay.tableExtraPay.quickAddRow.forEveryChildComponent(comp => comp.displayData(null, null, 0));
        if (clearCarrier) {
            this.mainDataSource.activeRow.removeLookupModelData("pending_payee_id");
            this.mainDataSource.activeRow.set("pending_payee_id", null);
            this.layoutMovementPay.textboxOverridePayRate.clear();
            this.layoutMovementPay.calculatePay(true, false, false);
        }
        this.layoutCarrierPrequalResult.hidePrequalPanels();
        this.panelIsoScorecard.visible = false;
    }

    private clearBoundData(panel: Panel) {
        panel.forEveryChildComponent(comp => {
            if ("textboxPendingPayeeId" != comp.id && comp.id != this.textboxCompanyTrailer.id && (comp instanceof Textbox || comp instanceof CityState) &&
                comp.enabled && !comp.printable && comp.field && this.mainDataSource.activeRow.get(comp.field) != null) {
                this.mainDataSource.activeRow.set(comp.field, null);
            }
        });
    }

    clearErrors(clearPayErrors: boolean = false) {
        this.layoutCommentsErrors.clearErrors();
        this.preventAssignment = false;
        this.qualError = null;
        if (!clearPayErrors)
            this.payError = null;
    }

    async validateCarrierForDispatch(carrierId: string, validatePay: boolean = false, clearPayErrors: boolean = false, showQualError: boolean = false, isNewCarrier: boolean = false): Promise<any> {
        this.clearErrors(clearPayErrors);
        this.savebuttonAssign.busy = true;
        this.layoutCommentsErrors.displayDispatchComments(carrierId);
        const contactName = isNewCarrier ?
            this.activeRow?.getFirstLookupModelData("pending_payee_id").get("contact.name") : this.activeRow.get("carrier_contact")
        const contactEmail = isNewCarrier ?
            this.activeRow?.getFirstLookupModelData("pending_payee_id").get("contact.email") : this.activeRow.get("carrier_email")
        return this.layoutCommentsErrors.displayValidationErrors(
            this.activeRow?.get("id"),
            carrierId,
            this.activeRow?.get("override_driver_nm"),
            this.activeRow?.get("override_driver2_nm"),
            contactName, contactEmail)
            .then(data => {
                this.preventAssignment = data?.errors.length > 0;
                this.qualError = !this.preventAssignment ? null : data?.errors;
                if (this.preventAssignment) {
                    if (this.qualError != null && showQualError) {
                        this.showDisqualSnackbar();
                    }
                }
            }).finally(() => {
                this.savebuttonAssign.busy = false;
                if (validatePay)
                    this.calculatePay(false);
                else
                    this.configureSaveButton();
            });
    }


    calculatePay(editedByUser: boolean, autoRate?: boolean, calculateFuelSurcharge?: boolean): Promise<any> {
        return this.layoutMovementPay.calculatePay(editedByUser, autoRate, false, calculateFuelSurcharge);
    }

    configureSaveButton() {
        let enableSave = true;
        this.savebuttonAssign.width = 160;
        if (this.requiresPendingAssignment && this.activeRow.get("override_payee_id") == null) {
            if (this.showSaveCaption()) {
                this.savebuttonAssign.caption = "Save";
            } else {
                this.savebuttonAssign.width = 200;
                this.savebuttonAssign.caption = "Pending Assignment";
            }
        } else if (this.activeRow.get("override_payee_id")) {
            this.savebuttonAssign.caption = "Next";
            enableSave = !this.preventAssignment || !this.requiresPendingAssignment
        } else {
            this.savebuttonAssign.caption = "Assign";
            enableSave = this.mainDataSource.activeRow.get("pending_payee_id") != null;
        }

        this.savebuttonAssign.enabled = enableSave && (!this.preventAssignment || this.requiresPendingAssignment);
    }

    private showSaveCaption(): boolean {
        return (this.origPendingPayeeId != null &&
            (this.origPendingPayeeId == this.mainDataSource.activeRow.get("pending_payee_id") ||
                this.mainDataSource.activeRow.get("pending_payee_id") == null)) ||
            (this.panelUnknownCarrier.visible &&
                this.origPendingCarrierNbr != null &&
                this.origPendingCarrierNbr == this.mainDataSource.activeRow.get("pending_carrier_nbr"));
    }

    async savebuttonAssignOnClick(event: ClickEvent) {
        // the following makes sure carrier pay is vaidated. If the user
        // modifies textboxOverridePayRate, doesn't tab out of the field,
        // and presses the save button, we end up posting the update before
        // validating carrier pay
        if (this.payError == null && this.activeRow.isNull("pending_carrier_nbr")) {
            this.calculatePay(false).then(() => {
                if (this.payError == null)
                    this.postUpdate(event.target as Button);
            });
        } else {
            await this.postUpdate(event.target as Button);
        }
    }

    get requiresPendingAssignment(): boolean {
        return this.qualError != null || this.payError != null || this.assigningToPendingCarrier;
    }

    private setPendingAssignmentLockFields() {
        this.mainDataSource.activeRow.set("lock_carrier_qual", this.qualError != null);
        this.mainDataSource.activeRow.set("lock_target_max_pay", this.payError != null);
        this.mainDataSource.activeRow.set("lock_new_carrier", this.assigningToPendingCarrier);
    }

    get assigningToPendingCarrier(): boolean {
        return this.panelUnknownCarrier.visible;
    }

    async postUpdate(button: Button) {
        button.busy = true;

        if (this.mainDataSource != null) {
            this.postingRow = true;
            if (!this.panelUnknownCarrier.visible)
                this.clearBoundData(this.panelUnknownCarrier);

            if (this.pendingAssignmentChanged()) {
                this.mainDataSource.activeRow.set("locked_user_id", null);
                this.mainDataSource.activeRow.set("unlock_time", null);
            }

            if (!this.requiresPendingAssignment && this.mainDataSource.activeRow.get("pending_payee_id") != null) {
                this.mainDataSource.activeRow?.set("override_payee_id", this.mainDataSource.activeRow.get("pending_payee_id"));
                this.mainDataSource.activeRow.setLookupModelData("override_payee_id",
                    this.mainDataSource.activeRow.getLookupModelData("pending_payee_id"));
                this.mainDataSource.activeRow.set("pending_payee_id", null);
                if (this.carrierOfferInfo) {
                    this.mainDataSource.activeRow.set("broker_avail_tract", this.carrierOfferInfo.broker_avail_tract);
                }
                this.textboxPendingPayeeId.dataSource = null;
                this.textboxPendingPayeeId.visible = false;
            } else {
                this.setPendingAssignmentLockFields();
            }

            if (!this.layoutMovementPay.activeRow.isNull('carrier_lane_rate_id')) {
                this.activeRow.set('carrier_lane_rate_id', this.layoutMovementPay.activeRow.getString('carrier_lane_rate_id'));
            }

            this.mainDataSource.execute().finally(() => {
                button.busy = false;
                this.postingRow = false;
                this.textboxPendingPayeeId.dataSource = this.sourceMovement;
                const pendingCarrierNbr = this.mainDataSource.activeRow.get("pending_carrier_nbr");
                if (!StringUtil.isEmptyString(pendingCarrierNbr)) {
                    Api.search("lme/dispatch/carrier",
                        this.textboxPendingCarrierIdType.text.includes("MC") ? { icc_number: this.mainDataSource.activeRow.get("pending_carrier_nbr") } :
                            this.textboxPendingCarrierIdType.text.includes("DOT") ? { dot_number: this.mainDataSource.activeRow.get("pending_carrier_nbr") } :
                                this.textboxPendingCarrierIdType.text.includes("Intrastate") ? { intrastate_code: this.mainDataSource.activeRow.get("pending_carrier_nbr") } : null
                    ).then(response => {
                        const carrierRow = response?.data?.[0];
                        if (!carrierRow) {
                            this.addOrInviteCarrier();
                        }
                    })
                }
            })
        }
    }

    addOrInviteCarrier() {
        if (this.requiresPendingAssignment
            && (!this.showSaveCaption() || this.pendingAssignmentChanged())) {
            if (this.auto_onboard_vendors.includes(this.vendor_id)) {
                Api.post("lme/powerbroker/prequal-carrier", {
                    identifier_type: this.textboxPendingCarrierIdType.text,
                    identifier_number: this.textboxPendingCarrierId.text
                }).then(response => {
                    const existInCarrierSystem = response?.data[0]?.exist_in_vendor_system;
                    const carrierAlreadyInvited = response?.data[0]?.carrier_invited;
                    const vendorName = response?.data[0]?.vendor_name;
                    if (!carrierAlreadyInvited) {
                        if (existInCarrierSystem) {
                            OnboardCarrier.showSlideout(this.mainDataSource.activeRow?.get("id"), response.data[0], false, this.textboxCarrierEmail.text, vendorName);
                        } else {
                            OnboardCarrier.showSlideout(this.mainDataSource.activeRow?.get("id"), null, false, this.textboxCarrierEmail.text, vendorName);
                        }
                    }
                });
            } else if (this.add_vendors.includes(this.vendor_id)) {
                AddCarrier.showSlideout(this.vendor_id, this.textboxPendingCarrierName.text,
                    this.textboxPendingCarrierIdType.text, this.textboxPendingCarrierId.text, this.carrierins_control_onboarding_server, this.packet_website_url);
            } else {
                OnboardCarrier.showSlideout(this.mainDataSource.activeRow?.get("id"), null, false, this.textboxCarrierEmail.text);
            }
        }
    }

    private pendingAssignmentChanged(): boolean {
        return (this.origPendingPayeeId != null && this.origPendingPayeeId != this.mainDataSource.activeRow.get("pending_payee_id")) ||
            (this.origPendingCarrierNbr != null && this.origPendingCarrierNbr != this.mainDataSource.activeRow.get("pending_carrier_nbr"));
    }

    close(canceled: boolean) {
        if (canceled) {
            this.layoutMovementPay.sourceExtraPay.data.forEach((row: ModelRow) => {
                if (!this.layoutMovementPay.cachedPayAdjustments.some((cachedRow: ModelRow) => cachedRow.get("id") === row.get("id"))) {
                    row.delete();
                }
            });
        }
        if (this.doAfterClose != null)
            this.doAfterClose(canceled);
    }

    showDisqualSnackbar() {
        let message = "This carrier does not meet the qualification requirements. ";

        if (this.origPendingPayeeId == null)
            message += "You may assign the carrier as Pending or select X to cancel.";
        else
            message += "You may update the Pending assignment or cancel it by removing the carrier.";


        const pnlContainer = new Panel({
            width: 450, rowBreak: false, padding: 0,
            components: [
                new Label({
                    text: "Carrier Qualifications",
                    align: HorizontalAlignment.CENTER,
                    fontBold: true,
                    fontSize: "medium"
                }),
                new Label({ text: message })
            ]
        })
        Snackbar.showWarningSnackbar(pnlContainer, { id: "carrierQual", persist: true });
        this.carrierQualSnackbar = ScreenStack.getOldestSnackbar("carrierQual") as Snackbar;
    }

    /** This is an event handler for the onClick event of buttonCancel.  */
    buttonCancelOnClick(event: ClickEvent) {
        this.close(true);
        this.mainDataSource.cancel();
    }

    textboxPendingCarrierIdTypeOnChange(event: ChangeEvent) {
        this.layoutCarrierPrequalResult.panelCarrierQualificationInfo.visible = false;
        this.layoutCarrierPrequalResult.panelCarrierAAQualificationInfo.visible = false;
        if (!StringUtil.isEmptyString(this.textboxPrequalVendor.text)) {
            this.layoutCarrierPrequalResult.configurePreQualBtn(this.textboxPendingCarrierId, this.textboxPendingCarrierIdType, this.textboxPrequalVendor, this.btnPrequalCarrier, this.vendor_id);
        }
        if (this.auto_onboard_vendors.includes(this.vendor_id) && !StringUtil.isEmptyString(this.textboxPendingCarrierId.text)) {
            const searchField = StringUtil.equalsIgnoreCase(event.newValue, "MC Number") ? "icc_number" :
                StringUtil.equalsIgnoreCase(event.newValue, "DOT Number") ? "dot_number" : null;
            if (searchField) {
                this.setPendingInviteLabel(this.textboxPendingCarrierId.text, searchField);
            } else {
                this.labelPendingInvitation.visible = false;
            }
        }
    }

    textboxPendingCarrierIdOnChange(event: ChangeEvent) {
        this.layoutCarrierPrequalResult.panelCarrierQualificationInfo.visible = false;
        this.layoutCarrierPrequalResult.panelCarrierAAQualificationInfo.visible = false;
        if (!StringUtil.isEmptyString(this.textboxPrequalVendor.text)) {
            this.layoutCarrierPrequalResult.configurePreQualBtn(this.textboxPendingCarrierId, this.textboxPendingCarrierIdType, this.textboxPrequalVendor, this.btnPrequalCarrier, this.vendor_id);
        }
    }

    textboxPendingCarrierIdOnBlur(event: BlurEvent) {

        if (StringUtil.isEmptyString(this.textboxPendingCarrierId.text)) {
            this.labelPendingInvitation.visible = false;
        } else {
            if (this.auto_onboard_vendors.includes(this.vendor_id)) {
                if (!StringUtil.isEmptyString(this.textboxPendingCarrierIdType.text) && StringUtil.equalsIgnoreCase("MC Number", this.textboxPendingCarrierIdType.text)) {
                    this.setPendingInviteLabel(this.textboxPendingCarrierId.text, "icc_number");
                } else if (StringUtil.equalsIgnoreCase(this.textboxPendingCarrierIdType.text, "DOT Number")) {
                    this.setPendingInviteLabel(this.textboxPendingCarrierId.text, "dot_number");
                }
            }
        }
    }


    async btnPrequalCarrierOnClick(event: ClickEvent) {
        this.btnPrequalCarrier.visible = false;
        this.prequalCarrier();
    }

    private prequalCarrier() {
        if (!StringUtil.isEmptyString(this.textboxPendingCarrierIdType.text) && !StringUtil.isEmptyString(this.textboxPendingCarrierId.text)) {
            this.layoutCarrierPrequalResult.prequalCarrier(this.textboxPendingCarrierIdType.text, this.textboxPendingCarrierId.text).then(response => {
            });
        }
    }

    private setLabelCarrierInvited(panel: Panel, dot_number: string, mc_number: string) {
        panel.forEveryChildComponent(comp => {
            if (StringUtil.equalsIgnoreCase("labelPendingInvitation", comp.id)) {
                CommonTooltips.setTooltipContentLayout(comp, null, "lme/powerbroker/CarrierInvitedQuickInfo", {
                    minWidth: 100,
                    minHeight: 100
                },
                { dot_number: dot_number, mc_number: mc_number });
                comp.tooltipDynamicOptions = { position: Alignment.RIGHT };
            }
        });
    }


    /** This is an event handler for the afterExecution event of sourceMovement.  */
    sourceMovementAfterExecution(event) {
        if (event._action == DataSourceMode.SEARCH && this.mainDataSource.activeRow != null) {
            this.checkForCarrierOfferInfo();
            this.origPendingPayeeId = this.mainDataSource.activeRow.get("pending_payee_id", null);
            this.origPendingCarrierNbr = this.mainDataSource.activeRow.get("pending_carrier_nbr");
            if (this.mainDataSource.activeRow.get("carrier_to_assign_id") != null) {
                this.mainDataSource.activeRow.set("pending_payee_id", this.mainDataSource.activeRow.get("carrier_to_assign_id"));
            } else if (this.origPendingCarrierNbr != null) {
                this.setPanelUnknownCarrierVisible(true, false);
            } else {
                this.layoutMovementPay.defaultPayUnitsAndMethod();
            }

            if (this.mainDataSource.activeRow.get("pending_payee_id") != null)
                this.sourceCarriersForAssignment.search({ "id": this.mainDataSource.activeRow.get("pending_payee_id") });

            this.mainDataSource.activeRow.addAfterFieldUpdateListener(event => this.fieldUpdated(event));
            const movementId = this.mainDataSource.activeRow.get("id");
            const manifestId = this.mainDataSource.activeRow.get("manifest_id");
            const subTitle = this.isIntercompany ? "Company" : "Carrier";
            const title = StringUtil.isEmptyString(manifestId) ? "Assign " + subTitle + " - Order " + this.mainDataSource.activeRow.get("orders.id", "") : "Assign Carrier - Manifest " + manifestId;
            if (movementId != null)
                this.labelHeaderTitle.text = title;
            this.setCarrierEmptyCityState();
            this.layoutMovementPay.layoutCarrierRates.layoutTargetPay.setupMpact(this.mainDataSource.activeRow);
            if (!StringUtil.isEmptyString(manifestId)) {
                this.layoutMovementPay.layoutCarrierRates.panelManifestMovementCharges.visible = true;
                this.layoutMovementPay.layoutCarrierRates.panelOrderCharges.visible = false;
            }
            if (this.mainDataSource.activeRow?.get("is_iso_active_and_licensed") && !StringUtil.isEmptyString(this.carrierOfferInfo?.payee_id)) {
                this.sourceCarrier.search({ "id": this.carrierOfferInfo.payee_id }).then(response => {
                    this.panelIsoScorecard.visible = response?.modelRows[0]?.data?.carrier_scorecard_exported;
                });
            } else {
                this.panelIsoScorecard.visible = false;
            }

        }
    }

    checkForCarrierOfferInfo() {
        if (!!this.carrierOfferInfo) {
            this.mainDataSource.activeRow.set("carrier_to_assign_id", this.carrierOfferInfo.payee_id);
            this.mainDataSource.activeRow.set("carrier_contact", this.carrierOfferInfo.carrier_contact);
            this.mainDataSource.activeRow.set("carrier_phone", this.carrierOfferInfo.carrier_phone);
            this.mainDataSource.activeRow.set("carrier_email", this.carrierOfferInfo.carrier_email);
            this.mainDataSource.activeRow.set("carrier_name", this.carrierOfferInfo.carrier_name);
            this.mainDataSource.activeRow.set("carrier_empty_from_city_id", this.carrierOfferInfo.carrier_empty_from_city_id);
            this.textboxPendingPayeeId.enabledDuringUpdate = false;
        }
    }

    /** This is an event handler for the onBlur event of citystateCarrierEmptyFromCityId.  */
    citystateCarrierEmptyFromCityIdOnBlur() {
        const cityId = this.sourceMovement.activeRow.get("carrier_empty_from_city_id") || this.citystateCarrierEmptyFromCityId?.textCombined?.getFirstLookupModelData()?.get("id");
        const moveId = this.mainDataSource.activeRow?.get("id");
        this.labelMilesToPickup.text = "";
        if (!StringUtil.isEmptyString(cityId) && moveId != null) {
            Api.post("lme/dispatch/get-next-stop-distance", {
                city_id: cityId,
                movement_id: moveId,
                use_load_dist_profile: true
            }).then(response => {
                if (!StringUtil.isEmptyString(response?.data[0]?.distance))
                    this.labelMilesToPickup.text = `${response?.data[0]?.distance} Miles to Pick-up`;
            });
        }
    }

    setCarrierEmptyCityState() {
        const cityId = this.sourceMovement.activeRow.get("carrier_empty_from_city_id");
        if (cityId)
            Api.search("common/city", { id: cityId }).then(result => {
                const data = result?.data[0];
                this.citystateCarrierEmptyFromCityId.textCombined.text = CityUtil.formatCityStateZip(data.name, data.state_id, data.zip_code);
                this.citystateCarrierEmptyFromCityIdOnBlur();
            });
    }

    static async checkCarrierForDataFusion(payeeId: String, moveId?: String): Promise<any> {
        let result = "";
        await Api.search("lme/datafusion/df-carrier-search", {
            payee_id: payeeId,
            move_id: moveId
        }).then(response => {
            result = response.data[0];
        });
        return result;
    }

    static async sendAutoTender(carrierId: string, moveId: string): Promise<any> {
        log.info("Sending AUTO tender");
        return await Api.post("lme/datafusion/auto-send-tender", {
            carrier: carrierId,
            move_id: moveId
        }).then(response => {
            return response;
        });
    }

    static async sendManualTender(carrierId: string, moveId: string, layout: SendLoadTender): Promise<any> {
        log.info("Sending MANUAL tender");
        const contacts = layout.textboxContact.text as unknown;
        const addlContacts = layout.textboxAddContact.text;
        const purpose = layout.labelPurpose.text;
        const mrb = layout.textboxMustRespondBy.text;
        let allContacts: Array<string>;
        let cString = "";

        if (!StringUtil.isEmptyString(contacts)) {
            allContacts = contacts as Array<string>;
        }

        if (!StringUtil.isEmptyString(allContacts)) {
            for (let i = 0; i < allContacts.length; i++) {
                if (i > 0) cString += ",";
                cString += allContacts[i];
            }
        }

        if (!StringUtil.isEmptyString(addlContacts)) {
            const addContactList: string[] = addlContacts.split(",");

            addContactList.forEach((item) => {
                if (!StringUtil.isEmptyString(cString)) cString += ",";
                cString += item as string;
            });
        }

        return await Api.post("lme/datafusion/send-tender", {
            carrier: carrierId, move_id: moveId, respond_by: mrb, purpose: purpose, contacts: cString
        });
    }

    public setDFLabel(carrierId: string, moveId?: string) {
        CarrierAssignment.checkCarrierForDataFusion(carrierId, moveId).then(result => {
            this.dfLabel.visible = (result.is_datafusion_carrier == "auto" || result.is_datafusion_carrier == "manual");
        });
    }

    async buttonDownloadImagesOnClick(event: ClickEvent) {
        const fileType: string | undefined = this.textboxFileType.selectedItem?.value;
        const docTypes: string[] = [];
        const panelSmc3Documents: Panel = this.panelSmc3Documents;
        const components: Component[] = panelSmc3Documents?.components;
        components?.forEach((component) => {
            if (component instanceof Checkbox && component.checked) {
                docTypes.push(component.valueChecked);
            }
        });

        const orderId = this.activeRow.get("orders.id");
        const proNumber = this.activeRow.get("pro_nbr");

        if (!orderId) {
            Snackbar.showWarningSnackbar("Order Id must be present to retrieve the document.");
            return;
        }

        if (!proNumber) {
            Snackbar.showWarningSnackbar("Pro number must be present to retrieve the document.");
            return;
        }

        if (!fileType) {
            Snackbar.showWarningSnackbar("File type must be selected to retrieve the document.");
            return;
        }

        if (docTypes.length === 0) {
            Snackbar.showWarningSnackbar("At least one document type must be selected to retrieve the document.");
            return;
        }

        this.buttonDownloadImages.busy = true;
        const inputs = { order_id: orderId, document_types: docTypes.join(","), pro_number: proNumber, file_type: fileType};

        try {
            const response = await Api.post("lme/powerbroker/smc3-document-retrieval", inputs);
            const data = response.data?.[0];
            if (!data) {
                Snackbar.showWarningSnackbar("No data returned from server.");
                return;
            }
            const imageList: Array<any> = data.image_list ?? [];
            const filenameList: Array<any> = data.filename_list ?? [];

            if (imageList.length === 0) {
                Snackbar.showWarningSnackbar(data.message);
                return;
            }
            imageList.forEach((imageData, index) => {
                const fileName = filenameList[index];
                FileUtil.downloadBase64AsFile(imageData, fileName);
                Snackbar.showSnackbar("Downloaded " + fileName, { millisUntilDismissal: 5000 });
            });
        } catch (error) {
            Snackbar.showWarningSnackbar("Failed to download documents. Please try again.");
        } finally {
            this.buttonDownloadImages.busy = false;
        }
    }

    async buttonGetTrackingStatusOnClick(event: ClickEvent) {
        const statusButton: Button = event.target as Button;
        statusButton.busy = true;
        await this.mainDataSource.post();
        Api.post("lme/powerbroker/smc3-status-tracking", {
            order_id: this.activeRow.get("orders.id", "")
        }).then(response => {
            if (response.data[0]?.["message"]) {
                Snackbar.showSnackbar(response.data[0]["message"]);
            }
            this.layoutRecentCallins.doSearch(this.mainDataSource.activeRow?.get("id"));
            if (response.data[0]?.["move_pro_nbr"]) {
                const textboxProNumber: Textbox = this.panelReference.findComponentById("textboxCarrierProNum") as Textbox;
                textboxProNumber.text = response.data[0]["move_pro_nbr"];
            }
        }).finally(() => statusButton.busy = false);
    }

    textboxCompanyTrailerBeforeLookupModelSearch(event) {
        event.filter.is_active = 'A';
    }

    private async setPendingInviteLabel(identifier: string, searchFilter: string) {
        if (StringUtil.isEmptyString(this.vendor_id)) {
            const result = await this.sourceCarrierInsCtrl.search();
            if (!StringUtil.isEmptyString(result?.modelRows[0]?.data?.vendor_id)) {
                this.vendor_id = result?.modelRows[0]?.data?.vendor_id;
            }
        }


        if (this.auto_onboard_vendors.includes(this.vendor_id)) {
            this.sourceInvitedCarrier.search({ [searchFilter]: identifier }).then(result => {
                this.labelPendingInvitation.visible = result?.modelRows[0]?.data?.carrier_previously_invited;
                if (this.labelPendingInvitation.visible) {
                    this.setLabelCarrierInvited(this.panelUnknownCarrier, result?.modelRows[0]?.data?.dot_number, result?.modelRows[0]?.data?.icc_number);
                }
            });
        } else {
            this.labelPendingInvitation.visible = false;
        }
    }

    async buttonRestartTrackingOnClick(event: ClickEvent) {
        let refList: string = "";
        const dialogProps = {
            yesButtonCaption: "Continue",
            noButtonCaption: "Cancel"
        };
        await Api.post("lme/powerbroker/smc3-initiate-tracking-manager", {
            order_id: this.activeRow.get("orders.id")
        }).then((response) => {
            refList = response.data[0]?.["reference_numbers"].map(obj => {
                const [key, value] = Object.entries(obj)[0];
                return `${key}:  ${value}`;
            }).join('\n');
        });
        const message: string = "The following reference numbers will be used:\n\n" + refList + "\n\n";
        const answer = await CommonDialogs.showYesNo(message, "Relaunch?", dialogProps);
        if (answer !== true)
            return;

        const statusButton: Button = event.target as Button;
        statusButton.busy = true;
        Api.post("lme/powerbroker/smc3-status-tracking", {
            order_id: this.activeRow.get("orders.id", ""),
            initiate_polling: true
        }).then(response => {
            if (response.data[0]?.["message"]) {
                Snackbar.showSnackbar(response.data[0]["message"]);
            }
        }).finally(() => statusButton.busy = false);
    }
}

function setVendorDesc(vendorId: string): string {
    let vendorDesc: string = "";

    if (vendorId === 'R') {
        vendorDesc = 'Registry Monitoring Service';
    } else if (vendorId === 'D') {
        vendorDesc = 'MyCarrierPortal';
    } else if (vendorId === 'H') {
        vendorDesc = 'Highway';
    } else if (vendorId === 'C') {
        vendorDesc = 'Carrier Watch';
    }

    return vendorDesc;
}
